import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, CircularProgress, useTheme, Box, Link, Collapse, Button, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DOMPurify from 'dompurify';
import Advertisement336 from './advertisement/Ad_GA_336';

interface WikipediaViewerProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

type TocItem = { id: string; text: string };
type ProcessedArticle = {
  content: string;
  toc: TocItem[];
  infoboxContent: string;
};

// 最後に表示した記事だけをセッション内に保持。
// 同じ記事を閉じてすぐ開き直すケースをカバー（再 fetch + 再パース回避）。
let lastArticle: { key: string; article: ProcessedArticle } | null = null;

function processWikipediaHtml(rawHtml: string, isMobile: boolean): ProcessedArticle {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');

  // 不要要素を削除
  const elementsToRemove = doc.querySelectorAll(
    '.mw-empty-elt, .mw-editsection, .error, .noprint, .pathnavbox, .navbox, .navbar, [role="presentation"]'
  );
  elementsToRemove.forEach((el) => el.remove());

  // 広告プレースホルダー挿入
  const sections = doc.querySelectorAll('section[data-mw-section-id]');
  const adPositions = isMobile ? [1, 4, 7, 11, 15] : [1, 5, 10, 16];
  sections.forEach((section, index) => {
    if (adPositions.includes(Number(section.getAttribute('data-mw-section-id')))) {
      const adDiv = doc.createElement('div');
      adDiv.id = `ad-container-${index}`;
      section.parentNode?.insertBefore(adDiv, section.nextSibling);
    }
  });

  // 外部リンクセクション以降を削除
  const externalLinks =
    doc.querySelector('#外部リンク') || doc.querySelector('#関連項目') || doc.querySelector('#脚注');
  if (externalLinks) {
    while (externalLinks.nextSibling) externalLinks.nextSibling.remove();
    externalLinks.remove();
  }

  // h2 に ID 付与 + TOC を構造化データで構築
  const toc: TocItem[] = [];
  doc.querySelectorAll('h2').forEach((el, index) => {
    const id = `wiki-h2-${index}`;
    el.id = id;
    toc.push({ id, text: el.textContent || '' });
  });

  // 全 <a> を DOM 直接操作で書換（inline onclick 注入を排除し、イベント委譲で処理）
  doc.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') || '';
    if (href.startsWith('./')) {
      const m = href.match(/^\.\/([^#]+)(#(.+))?$/);
      if (!m) return;
      const wikiPath = m[1];
      const anchor = m[3];
      if (anchor) {
        // 同じ Wikipedia 記事内のアンカー → 内部スクロール
        a.setAttribute('href', `#${anchor}`);
        a.setAttribute('data-wiki-anchor', anchor);
      } else {
        // 別 Wikipedia 記事 → 新タブで開く
        a.setAttribute('href', `https://ja.wikipedia.org/wiki/${wikiPath}`);
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      }
    } else if (/^https?:\/\//.test(href)) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    }
  });

  // infobox を分離
  let infoboxContent = '';
  const infobox = doc.querySelector('.infobox');
  if (infobox) {
    infoboxContent = DOMPurify.sanitize(infobox.outerHTML, { ADD_ATTR: ['target'] });
    infobox.remove();
  }

  // 本文を sanitize（target 属性を保持）
  const content = DOMPurify.sanitize(doc.body.innerHTML, { ADD_ATTR: ['target'] });

  return { content, toc, infoboxContent };
}

const WikipediaViewer: React.FC<WikipediaViewerProps> = ({ open, onClose, title }) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [toc, setToc] = useState<TocItem[]>([]);
  const [infoboxContent, setInfoboxContent] = useState<string>('');
  const [showInfobox, setShowInfobox] = useState<boolean>(false);
  const theme = useTheme();
  const [currentSection, setCurrentSection] = useState<string>('');
  const [showFullToc, setShowFullToc] = useState<boolean>(false);
  const [adContainers, setAdContainers] = useState<Element[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!open) return;

    const cacheKey = `${title}:${isMobile ? 'm' : 'd'}`;
    if (lastArticle?.key === cacheKey) {
      const { article } = lastArticle;
      setContent(article.content);
      setToc(article.toc);
      setInfoboxContent(article.infoboxContent);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const controller = new AbortController();

    fetch(`https://ja.wikipedia.org/api/rest_v1/page/html/${encodeURIComponent(title)}`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('ページが見つかりません');
        return res.text();
      })
      .then((html) => {
        const article = processWikipediaHtml(html, isMobile);
        lastArticle = { key: cacheKey, article };
        setContent(article.content);
        setToc(article.toc);
        setInfoboxContent(article.infoboxContent);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('Wikipedia APIからのデータ取得に失敗しました:', err);
        setError(err.message);
        setLoading(false);
      });

    return () => controller.abort();
  }, [open, title, isMobile]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrentSection(entry.target.textContent || '');
          }
        });
      },
      { threshold: 0.5, rootMargin: '-60px 0px 0px 0px' }
    );

    const headings = contentRef.current?.querySelectorAll('h2, h3');
    headings?.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [content]);

  useEffect(() => {
    // Portal で広告を挿入する対象 div 群を取得
    // (createRoot だと別ツリーになり Context が切れるため)
    const containers = Array.from(
      contentRef.current?.querySelectorAll('[id^="ad-container-"]') || []
    );
    setAdContainers(containers);
  }, [content]);

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleTocClick = (id: string) => {
    scrollToId(id);
    setShowFullToc(false);
  };

  // 本文内の data-wiki-anchor リンク を イベント委譲で処理
  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchorEl = target.closest('[data-wiki-anchor]');
    if (anchorEl) {
      e.preventDefault();
      const anchorId = anchorEl.getAttribute('data-wiki-anchor');
      if (anchorId) scrollToId(anchorId);
    }
  };

  const tocLinkSx = {
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  };

  const renderTocList = () => (
    <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
      {toc.map((item) => (
        <Box component="li" key={item.id} sx={{ mb: 0.25 }}>
          <Link
            href={`#${item.id}`}
            onClick={(e) => {
              e.preventDefault();
              handleTocClick(item.id);
            }}
            sx={tocLinkSx}
          >
            {item.text}
          </Link>
        </Box>
      ))}
    </Box>
  );

  const renderToc = () => (
    <Box>
      <Typography
        variant="h6"
        onClick={() => setShowFullToc(!showFullToc)}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {currentSection || '目次'}
        <ExpandMoreIcon
          sx={{ ml: 1, transform: showFullToc ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
        />
      </Typography>
      <Collapse in={showFullToc}>{renderTocList()}</Collapse>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          width: '100%',
          maxWidth: isMobile ? '100%' : '1280px',
          borderRadius: isMobile ? '0' : '12px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {isMobile && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: '8px 16px',
          }}
        >
          {renderToc()}
        </Box>
      )}
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          backgroundColor: theme.palette.background.default,
          padding: isMobile ? '16px' : '24px',
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              flex: '0 0 200px',
              position: 'sticky',
              top: 0,
              maxHeight: '80vh',
              overflowY: 'auto',
              marginRight: '16px',
            }}
          >
            <Typography variant="h6">目次</Typography>
            {renderTocList()}
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            maxWidth: isMobile ? '100%' : 'calc(100% - 216px)',
          }}
          ref={contentRef}
        >
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <Box
              sx={{
                '& a': {
                  color: theme.palette.text.secondary,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                },
                '& h2': {
                  fontSize: { xs: '1.3rem', sm: '1.4rem' },
                  fontWeight: 'normal',
                  marginTop: '2rem',
                  marginBottom: '1rem',
                  borderBottom: '2px solid',
                  borderColor: theme.palette.divider,
                  paddingBottom: '0.25rem',
                },
                '& h3': {
                  fontSize: { xs: '1.1rem', sm: '1.2rem' },
                  fontWeight: 'bold',
                  marginTop: '1rem',
                  marginBottom: '0.5rem',
                },
                '& h4': {
                  fontSize: '.95rem',
                  fontWeight: 'bold',
                  marginTop: '1rem',
                  marginBottom: '0.8rem',
                },
                '& dt': { marginTop: '0.5rem' },
                '& ul': { paddingLeft: '1rem' },
                '& li': { marginBottom: '0.25rem' },
                '& p': {
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  marginBottom: '0.5rem',
                  lineHeight: '1.6',
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginBottom: '1rem',
                  display: 'block',
                  overflowX: 'auto',
                  maxWidth: '100%',
                },
                '& th, & td': {
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  padding: '8px',
                  textAlign: 'left',
                  backgroundColor: theme.palette.background.paper,
                  fontSize: { xs: '0.65rem', sm: '0.8rem' },
                  minWidth: '100px',
                },
                '& th': {
                  backgroundColor: theme.palette.background.default + ' !important',
                  color: theme.palette.text.primary + ' !important',
                  whiteSpace: 'nowrap',
                },
              }}
              onClick={handleContentClick}
            >
              <div dangerouslySetInnerHTML={{ __html: content }} />
              {adContainers.map((container, idx) =>
                createPortal(<Advertisement336 />, container, `wiki-ad-${idx}`)
              )}
              <Collapse in={showInfobox}>
                <div dangerouslySetInnerHTML={{ __html: infoboxContent }} />
              </Collapse>
              <Button onClick={() => setShowInfobox(!showInfobox)}>
                infoboxを{showInfobox ? '閉じる' : '開く'}
              </Button>
              <Typography variant="body2" sx={{ mt: 2, textAlign: 'right' }}>
                <Link
                  href={`https://ja.wikipedia.org/wiki/${encodeURIComponent(title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: '0.8rem' }}
                >
                  Source: Wikipedia
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WikipediaViewer;
